import { useState } from 'react';
import { Building2, ArrowRight } from 'lucide-react';

const BOARDS = ['CBSE', 'ICSE', 'State Board', 'IB', 'Cambridge'];
const GRADES = ['6', '7', '8', '9', '10', '11', '12'];
const SECTIONS = ['A', 'B', 'C', 'D', 'E', 'F'];

export default function Step1_BasicInfo({ onNext, onSubmit, loading, schoolType = 'school' }) {
  const isCoaching = schoolType === 'coaching';
  
  const [formData, setFormData] = useState({
    name: '',
    board: 'CBSE',
    currentYear: new Date().getFullYear().toString(),
    grades: ['6', '7', '8', '9', '10'],
    sections: ['A', 'B'],
  });

  const handleGradeToggle = (grade) => {
    setFormData(prev => ({
      ...prev,
      grades: prev.grades.includes(grade)
        ? prev.grades.filter(g => g !== grade)
        : [...prev.grades, grade]
    }));
  };

  const handleSectionToggle = (section) => {
    setFormData(prev => ({
      ...prev,
      sections: prev.sections.includes(section)
        ? prev.sections.filter(s => s !== section)
        : [...prev.sections, section]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await onSubmit(formData);
      onNext(formData);
    } catch (err) {
      // Error handled in parent
    }
  };

  return (
    <div>
      <div className="flex items-center mb-6">
        <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mr-4">
          <Building2 className="w-6 h-6 text-blue-600" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Basic Information</h2>
          <p className="text-gray-600">
            {isCoaching ? 'Tell us about your coaching center' : 'Tell us about your school'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* School Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {isCoaching ? 'Coaching Center Name *' : 'School Name *'}
          </label>
          <input
            type="text"
            required
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder={isCoaching ? "Enter coaching center name" : "Enter school name"}
          />
        </div>

        {/* Board */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Education Board *
          </label>
          <select
            required
            value={formData.board}
            onChange={(e) => setFormData(prev => ({ ...prev, board: e.target.value }))}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {BOARDS.map(board => (
              <option key={board} value={board}>{board}</option>
            ))}
          </select>
        </div>

        {/* Academic Year */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Academic Year *
          </label>
          <input
            type="text"
            required
            value={formData.currentYear}
            onChange={(e) => setFormData(prev => ({ ...prev, currentYear: e.target.value }))}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="2024-25"
          />
        </div>

        {/* Grades */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Grades *
          </label>
          <div className="flex flex-wrap gap-2">
            {GRADES.map(grade => (
              <button
                key={grade}
                type="button"
                onClick={() => handleGradeToggle(grade)}
                className={`px-4 py-2 rounded-lg border-2 transition-all ${
                  formData.grades.includes(grade)
                    ? 'border-blue-600 bg-blue-600 text-white'
                    : 'border-gray-300 hover:border-blue-400'
                }`}
              >
                Grade {grade}
              </button>
            ))}
          </div>
        </div>

        {/* Sections - Only for schools, not coaching centers */}
        {!isCoaching && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sections *
            </label>
            <div className="flex flex-wrap gap-2">
              {SECTIONS.map(section => (
                <button
                  key={section}
                  type="button"
                  onClick={() => handleSectionToggle(section)}
                  className={`w-12 h-12 rounded-lg border-2 transition-all ${
                    formData.sections.includes(section)
                      ? 'border-blue-600 bg-blue-600 text-white'
                      : 'border-gray-300 hover:border-blue-400'
                  }`}
                >
                  {section}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Submit Button */}
        <div className="flex justify-end pt-4">
          <button
            type="submit"
            disabled={loading || !formData.name || formData.grades.length === 0 || (!isCoaching && formData.sections.length === 0)}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading ? 'Saving...' : 'Continue'}
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </form>
    </div>
  );
}
